import { useState } from "react";
import { Send, Share2, Undo2 } from "lucide-react";
import { KitModal } from "@/app/routes/dashboard/components/kit/Modal";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { KitCheckbox } from "@/app/routes/dashboard/components/kit/Checkbox";
import type { StrategyGuestShare } from "@/types/strategyShare.type";
import {
  useRevokeStrategyShare,
  useSendStrategyShares,
  useStrategyShares,
} from "../api/useStrategyShares";

/**
 * Send the strategy to the brand's guests — the client's people, each with
 * their own account. Guests have no strategy of their own: they read exactly
 * the ones sent to them, under Strategy, view only. Sending also emails them a
 * link into their account. A guest keeps it until it is taken back here.
 */

function RowSkeletons() {
  return (
    <div className="flex flex-col gap-2.5" aria-hidden="true">
      {[0, 1].map((i) => (
        <div key={i} className="flex items-center justify-between rounded-card bg-co-surface p-3.5">
          <div className="flex flex-col gap-2">
            <div className="h-3.5 w-40 animate-pulse rounded-tag bg-co-hover" />
            <div className="h-3 w-56 animate-pulse rounded-tag bg-co-hover" />
          </div>
          <div className="h-7 w-24 animate-pulse rounded-tag bg-co-hover" />
        </div>
      ))}
    </div>
  );
}

function ShareRow({
  share,
  canSend,
  onResend,
  onRevoke,
}: {
  share: StrategyGuestShare;
  canSend: boolean;
  onResend: () => void;
  onRevoke: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-card bg-co-surface p-3.5 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="truncate text-[13.5px] font-semibold text-co-text">{share.name || share.email}</p>
        <p className="mt-0.5 truncate text-[11.5px] text-co-muted">
          {share.name ? `${share.email} · ` : ""}
          {share.lastViewedAt
            ? `last opened ${new Date(share.lastViewedAt).toLocaleDateString()}`
            : "not opened yet"}
        </p>
        <p className="mt-0.5 truncate text-[11px] text-co-faint">
          sent {new Date(share.sentAt).toLocaleDateString()}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {canSend ? (
          <KitButton
            variant="secondary"
            iconLeft={Send}
            onClick={onResend}
            title="Email them again — the strategy stays in their account either way"
          >
            Email again
          </KitButton>
        ) : null}
        <KitButton
          variant="secondary"
          iconLeft={Undo2}
          onClick={onRevoke}
          title="They will no longer see this strategy"
          className="!text-co-alert-ink"
        >
          Take back
        </KitButton>
      </div>
    </div>
  );
}

export default function ShareStrategyModal({
  brandId,
  strategyId,
  strategyTitle,
  onClose,
}: {
  brandId: string;
  strategyId: string;
  strategyTitle: string;
  onClose: () => void;
}) {
  const { data, isLoading, isError, refetch } = useStrategyShares(brandId, strategyId);
  const sendShares = useSendStrategyShares(brandId, strategyId);
  const revokeShare = useRevokeStrategyShare(brandId, strategyId);
  const [selected, setSelected] = useState<string[]>([]);

  const sentTo = new Set((data?.shares ?? []).map((s) => s.userId));
  const unsent = (data?.guests ?? []).filter((g) => !sentTo.has(g.userId));
  const shareable = data?.shareable ?? false;

  const send = () => {
    const userIds = selected.filter((id) => unsent.some((g) => g.userId === id));
    if (userIds.length === 0) return;
    sendShares.mutate(userIds);
    setSelected([]);
  };

  return (
    <KitModal
      isOpen
      onOpenChange={(open) => !open && onClose()}
      icon={Share2}
      title="Send this strategy to the client"
      description={`Send ${strategyTitle} to the brand's guests. They read it under Strategy in their own account — view only, nothing they can change.`}
      modalClassName="max-w-[640px]"
    >
      <div className="flex flex-col gap-6">
        <p className="rounded-card bg-co-surface px-3.5 py-2.5 text-[12px] text-co-muted">
          A guest keeps the strategy until you take it back. It stops showing on its own if it goes
          back to draft or gets replaced by a newer version.
        </p>

        {isLoading ? (
          <RowSkeletons />
        ) : isError || !data ? (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-card bg-co-surface px-4 py-3">
            <p className="text-[13px] text-co-muted">The guest list could not load.</p>
            <KitButton variant="ghost" onClick={() => void refetch()}>
              Retry
            </KitButton>
          </div>
        ) : (
          <>
            <section className="flex flex-col gap-3">
              <p className="text-[12px] font-medium uppercase tracking-[0.12em] text-co-faint">Send to</p>
              {!shareable ? (
                <p className="rounded-card bg-co-surface px-4 py-6 text-center text-[13px] text-co-faint">
                  Only an active strategy can be sent. Make this one active first.
                </p>
              ) : data.guests.length === 0 ? (
                <p className="rounded-card bg-co-surface px-4 py-6 text-center text-[13px] text-co-faint">
                  This brand has no guests yet. Invite the client as a guest from the calendar&apos;s Guests
                  panel, then send it to them here.
                </p>
              ) : unsent.length === 0 ? (
                <p className="text-[13px] text-co-faint">Every guest of this brand already has it.</p>
              ) : (
                <div className="flex flex-col gap-3">
                  <div className="border-t border-co-line">
                    {unsent.map((guest) => (
                      <KitCheckbox
                        key={guest.userId}
                        isSelected={selected.includes(guest.userId)}
                        onChange={(on) =>
                          setSelected((cur) => (on ? [...cur, guest.userId] : cur.filter((id) => id !== guest.userId)))
                        }
                        className="flex w-full border-b border-co-line py-3"
                      >
                        <span className="min-w-0 truncate">
                          <span className="tt-read-s font-semibold text-co-text">{guest.name || guest.email}</span>
                          {guest.name ? <span className="tt-read-s text-co-faint"> · {guest.email}</span> : null}
                        </span>
                      </KitCheckbox>
                    ))}
                  </div>
                  <div className="flex justify-end">
                    <KitButton iconLeft={Send} onClick={send} isDisabled={selected.length === 0}>
                      {selected.length > 0 ? `Send to ${selected.length}` : "Send"}
                    </KitButton>
                  </div>
                </div>
              )}
            </section>

            <section className="flex flex-col gap-3">
              <p className="text-[12px] font-medium uppercase tracking-[0.12em] text-co-faint">Sent to</p>
              {data.shares.length === 0 ? (
                <div className="rounded-card bg-co-surface px-4 py-8 text-center">
                  <p className="text-[13px] text-co-faint">No guest has this strategy yet.</p>
                </div>
              ) : (
                <div className="flex flex-col gap-2.5">
                  {data.shares.map((share) => (
                    <ShareRow
                      key={share.userId}
                      share={share}
                      canSend={shareable}
                      onResend={() => sendShares.mutate([share.userId])}
                      onRevoke={() => revokeShare.mutate(share.userId)}
                    />
                  ))}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </KitModal>
  );
}
